import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, CardHeader,
  Chip, LinearProgress, Button, Alert, IconButton, Tabs, Tab, Tooltip, Stack
} from '@mui/material';
import { Refresh, CheckCircle, Error, HourglassEmpty, Description, Email, Delete } from '@mui/icons-material';
import { useNotification } from '../hooks/useNotification';
import { useJobProgress } from '../hooks/useJobProgress';
import { useUserSettings } from '../hooks/useUserSettings';
import { jobsApi } from '../services/api';

const ProcessingJobs = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useNotification();
  const { canChooseModels } = useUserSettings();
  const [pdfJobs, setPdfJobs] = useState([]);
  const [emailJobs, setEmailJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Check URL params to set initial tab (tab=email from received emails page)
  const initialTab = searchParams.get('tab') === 'email' ? 1 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  
  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔵 ProcessingJobs: Fetching jobs via jobsApi');
      
      const [pdfResponse, emailResponse] = await Promise.all([
        jobsApi.getAll(),
        jobsApi.getEmailJobs().catch(() => ({ data: { jobs: [] } })) // Gracefully handle if not available
      ]);
      
      console.log('🔵 ProcessingJobs: PDF jobs:', pdfResponse.data);
      console.log('🔵 ProcessingJobs: Email jobs:', emailResponse.data);
      
      setPdfJobs(pdfResponse.data.jobs || []);
      setEmailJobs(emailResponse.data.jobs || []);
    } catch (error) {
      console.error('🔴 ProcessingJobs: Error loading jobs:', error);
      console.error('🔴 ProcessingJobs: Error message:', error.message);
      console.error('🔴 ProcessingJobs: Error response:', error.response);
      showError('Failed to load processing jobs');
    } finally {
      setLoading(false);
    }
  }, [showError]);
  
  const handleCancelJob = useCallback(async (jobId, jobType) => {
    if (!window.confirm('Cancel this job? No pages will be charged.')) {
      return;
    }

    try {
      if (jobType === 'email') {
        await jobsApi.cancelEmailJob(jobId);
      } else {
        await jobsApi.cancel(jobId);
      }
      showSuccess('Job cancelled successfully (no pages charged)');
      loadJobs(); // Refresh the list
    } catch (error) {
      console.error('Error cancelling job:', error);
      showError('Failed to cancel job');
    }
  }, [showSuccess, showError, loadJobs]);

  const handleDismissJob = useCallback(async (jobId, jobType, jobStatus) => {
    const isCompleted = jobStatus === 'completed';
    const isProcessing = ['pending', 'fetching', 'classifying', 'processing'].includes(jobStatus);
    
    let message;
    if (isCompleted) {
      message = 'Dismiss this job? Pages will be refunded.';
    } else if (isProcessing) {
      message = 'Cancel this running job?';
    } else {
      message = 'Dismiss this job?';
    }
    
    if (!window.confirm(message)) {
      return;
    }

    try {
      if (jobType === 'email') {
        const response = await jobsApi.cancelEmailJob(jobId);
        const refundMsg = response.data?.pagesRefunded > 0 
          ? ` (${response.data.pagesRefunded} pages refunded)` 
          : '';
        showSuccess(isProcessing ? 'Job cancelled' : `Job dismissed${refundMsg}`);
      } else {
        await jobsApi.cancel(jobId);
        showSuccess(isProcessing ? 'Job cancelled' : 'Job dismissed');
      }
      loadJobs(); // Refresh the list
    } catch (error) {
      console.error('Error dismissing job:', error);
      showError('Failed to dismiss job');
    }
  }, [showSuccess, showError, loadJobs]);
  
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);
  
  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle color="success" />;
      case 'failed': return <Error color="error" />;
      case 'processing': return <HourglassEmpty color="warning" />;
      default: return <HourglassEmpty color="info" />;
    }
  };
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'warning';
      default: return 'info';
    }
  };

  // Component to render a single job with SSE progress updates
  const JobCard = ({ job, jobType }) => {
    const isProcessing = ['pending', 'fetching', 'classifying', 'processing'].includes(job.status);
    const { progress } = useJobProgress(job.id, jobType, isProcessing);
    
    // Use SSE progress if available, otherwise use job data
    // If SSE not yet connected, show loading state for progress fields
    const currentData = progress || job;
    const hasSSEData = progress !== null;
    
    // Show dismiss button for all jobs (processing ones will be cancelled)
    const canDismiss = true;
    
    return (
      <Card key={job.id} sx={{ mb: 2 }}>
        <CardHeader
          avatar={getStatusIcon(currentData.status)}
          title={
            jobType === 'pdf' 
              ? (job.file?.originalFilename || 'File') 
              : `Email Job (${new Date(job.dateFrom).toLocaleDateString()} - ${new Date(job.dateTo).toLocaleDateString()})`
          }
          subheader={
            // Model names are shown only to users who can choose them
            !canChooseModels
              ? `Started: ${new Date(job.createdAt).toLocaleString()}`
              : jobType === 'pdf'
                ? `Started: ${new Date(job.createdAt).toLocaleString()} | Model: ${job.model}`
                : `Started: ${new Date(job.createdAt).toLocaleString()} | Classification: ${job.classificationModel} | Categorization: ${job.categorizationModel}`
          }
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip 
                label={currentData.status.toUpperCase()} 
                color={getStatusColor(currentData.status)}
              />
              {canDismiss && (
                <Tooltip title="Dismiss job">
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismissJob(job.id, jobType, currentData.status);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          }
        />
        <CardContent>
          {jobType === 'pdf' ? (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Pages: {currentData.pagesProcessed}/{currentData.totalPages}
              </Typography>
              
              {currentData.status === 'processing' && currentData.totalPages > 0 && (
                <>
                  <LinearProgress 
                    variant="determinate" 
                    value={((currentData.pagesProcessed || 0) / currentData.totalPages) * 100} 
                    sx={{ my: 2 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(((currentData.pagesProcessed || 0) / currentData.totalPages) * 100)}% complete
                  </Typography>
                </>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {currentData.status === 'fetching' && `Fetching emails...`}
                {currentData.status === 'classifying' && `Classifying: ${currentData.emailsClassified || 0}/${currentData.totalEmailsToClassify || '?'} days`}
                {currentData.status === 'processing' && `Processing: ${currentData.emailsCategorized || 0}/${currentData.totalEmailsToProcess || '?'} emails`}
                {currentData.status === 'completed' && `Emails: ${currentData.emailsFetched} | Transaction emails: ${currentData.transactionEmailsFound} | Transactions extracted: ${currentData.transactionsExtracted}`}
                {currentData.status === 'failed' && `Failed at ${currentData.status}`}
              </Typography>
              
              {currentData.status === 'classifying' && currentData.totalEmailsToClassify > 0 && (
                <>
                  <LinearProgress 
                    variant={hasSSEData ? "determinate" : "indeterminate"}
                    value={hasSSEData && currentData.totalEmailsToClassify ? ((currentData.emailsClassified || 0) / currentData.totalEmailsToClassify) * 100 : undefined}
                    sx={{ my: 2 }}
                  />
                  {hasSSEData && currentData.totalEmailsToClassify && (
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(((currentData.emailsClassified || 0) / currentData.totalEmailsToClassify) * 100)}% classified
                    </Typography>
                  )}
                </>
              )}
              
              {currentData.status === 'processing' && currentData.totalEmailsToProcess > 0 && (
                <>
                  <LinearProgress 
                    variant={hasSSEData ? "determinate" : "indeterminate"}
                    value={hasSSEData && currentData.totalEmailsToProcess ? ((currentData.emailsCategorized || 0) / currentData.totalEmailsToProcess) * 100 : undefined}
                    sx={{ my: 2 }}
                  />
                  {hasSSEData && currentData.totalEmailsToProcess && (
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(((currentData.emailsCategorized || 0) / currentData.totalEmailsToProcess) * 100)}% processed
                    </Typography>
                  )}
                </>
              )}
              
              {currentData.status === 'fetching' && (
                <LinearProgress sx={{ my: 2 }} />
              )}
            </>
          )}
          
          {currentData.status === 'completed' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="success.main" gutterBottom>
                ✓ Found {jobType === 'pdf' ? currentData.transactionsFound : currentData.transactionsExtracted} transactions
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => navigate(`/review-job/${job.id}${jobType === 'email' ? '?type=email' : ''}`)}
              >
                Review & Approve
              </Button>
            </Box>
          )}
          
          {isProcessing && (
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="outlined" 
                color="warning"
                onClick={() => handleCancelJob(job.id, jobType)}
              >
                Cancel Job (No Charge)
              </Button>
            </Box>
          )}
          
          {currentData.status === 'failed' && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={currentData.errorMessage === 'Cancelled by user' ? 'warning' : 'error'} sx={{ mb: 2 }}>
                {currentData.errorMessage === 'Cancelled by user' 
                  ? '🛑 Cancelled: This job was cancelled by you'
                  : `Error: ${currentData.errorMessage || 'Processing failed'}`
                }
              </Alert>
              {currentData.errorMessage !== 'Cancelled by user' && (
                <Button 
                  variant="outlined" 
                  color="warning"
                  onClick={() => handleCancelJob(job.id, jobType)}
                >
                  Cancel Job (No Charge)
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };
  
  const renderJobList = (jobs, jobType) => {
    if (loading && jobs.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">Loading jobs...</Typography>
        </Box>
      );
    }

    if (jobs.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">
            {jobType === 'pdf' 
              ? 'No PDF processing jobs yet. Upload a file to get started!' 
              : 'No email processing jobs yet. Fetch emails to get started!'}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate(jobType === 'pdf' ? '/upload' : '/fetch-emails')}
            sx={{ mt: 2 }}
          >
            {jobType === 'pdf' ? 'Upload File' : 'Fetch Emails'}
          </Button>
        </Box>
      );
    }

    return jobs.map(job => (
      <JobCard key={job.id} job={job} jobType={jobType} />
    ));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Processing Jobs
        </Typography>
        <Tooltip title="Refresh jobs">
          <IconButton onClick={loadJobs} disabled={loading} color="primary">
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Processing jobs update in real-time. No need to refresh!
      </Alert>
      
      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label={`PDF Files (${pdfJobs.length})`} icon={<Description />} />
          <Tab label={`Email Jobs (${emailJobs.length})`} icon={<Email />} />
        </Tabs>
      </Card>
      
      {activeTab === 0 && renderJobList(pdfJobs, 'pdf')}
      {activeTab === 1 && renderJobList(emailJobs, 'email')}
    </Box>
  );
};

export default ProcessingJobs;

